import { generateToken } from "./GenerateToken";

export const KINPOS_QR_PRINT_CONTRACT = "smartpay.kinpos.qr-print.v1";
export const KINPOS_QR_PRINT_OPERATION = "print_table_prepay_qr";
export const KINPOS_QR_PRINT_MAX_VALUE_LENGTH = 4096;

const sanitizeText = (value, maxLength) => Array.from(String(value ?? ""), (character) => {
  const characterCode = character.charCodeAt(0);
  return characterCode <= 31 || characterCode === 127 ? " " : character;
}).join("").trim().slice(0, maxLength);

const createQrPrintError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const validateQrValue = (value) => {
  const hasMeaningfulContent = typeof value === "string" && Array.from(value).some((character) => {
    const characterCode = character.codePointAt(0);
    const isControlCharacter = characterCode <= 31
      || (characterCode >= 127 && characterCode <= 159);
    return !isControlCharacter && character.trim().length > 0;
  });

  if (
    typeof value !== "string"
    || value.length === 0
    || value.length > KINPOS_QR_PRINT_MAX_VALUE_LENGTH
    || !hasMeaningfulContent
  ) {
    throw createQrPrintError(
      "KINPOS_QR_PRINT_INVALID_REQUEST",
      "El valor del QR no es válido para imprimir."
    );
  }

  return value;
};

const normalizeAcknowledgement = (value) => {
  if (value === true) return { accepted: true };
  if (typeof value !== "string") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed === true ? { accepted: true } : parsed;
  } catch (error) {
    return null;
  }
};

// Generic table-QR print request - source/subtitle/footer are overridable so callers (prepay,
// pospago, ...) can print their own accurate copy while still sending the exact same
// `operation`/`contract` the Android app was built to recognize, which is what actually has to
// match for printing to work.
export const buildTableQrPrintRequest = ({
  qrValue,
  tableId,
  tableName,
  requestId = `qr-print-${generateToken()}`,
  source = "table_prepay",
  subtitle = "Escanea, pide y paga",
  footer = "Válido para un solo uso",
} = {}) => {
  const exactQrValue = validateQrValue(qrValue);
  const safeTableId = sanitizeText(tableId, 128);
  const safeTableName = sanitizeText(tableName, 128);
  const safeRequestId = sanitizeText(requestId, 128);

  if (!safeTableId || !safeRequestId) {
    throw createQrPrintError(
      "KINPOS_QR_PRINT_INVALID_REQUEST",
      "Faltan datos para imprimir el QR de la mesa."
    );
  }

  return {
    contract: KINPOS_QR_PRINT_CONTRACT,
    operation: KINPOS_QR_PRINT_OPERATION,
    request_id: safeRequestId,
    source,
    table: {
      id: safeTableId,
      name: safeTableName,
    },
    qr: {
      value: exactQrValue,
      error_correction: "M",
    },
    content: {
      title: safeTableName ? `Mesa ${safeTableName}` : "Mesa",
      subtitle,
      footer,
    },
    copies: 1,
  };
};

export class KinposQrPrintBridge {
  constructor({ windowObject } = {}) {
    this.windowObject = windowObject;
  }

  getWindow() {
    if (this.windowObject) return this.windowObject;
    return typeof window !== "undefined" ? window : null;
  }

  isAvailable() {
    return typeof this.getWindow()?.AndroidBridge?.printQr === "function";
  }

  printTableQr(data) {
    if (!this.isAvailable()) {
      throw createQrPrintError(
        "KINPOS_QR_PRINT_BRIDGE_UNAVAILABLE",
        "La impresión QR del PAX no está disponible en este dispositivo."
      );
    }

    const request = buildTableQrPrintRequest(data);
    let acknowledgement;
    try {
      acknowledgement = normalizeAcknowledgement(
        this.getWindow().AndroidBridge.printQr(JSON.stringify(request))
      );
    } catch (error) {
      throw createQrPrintError(
        "KINPOS_QR_PRINT_INVOCATION_ERROR",
        "Android no pudo recibir la impresión del QR."
      );
    }

    if (
      acknowledgement?.accepted !== true
      || (
        acknowledgement.request_id
        && acknowledgement.request_id !== request.request_id
      )
    ) {
      throw createQrPrintError(
        "KINPOS_QR_PRINT_REJECTED",
        "Android rechazó la impresión del QR."
      );
    }

    return { request, acknowledgement };
  }
}

export const kinposQrPrintBridge = new KinposQrPrintBridge();
